<script setup lang="ts">
import { defineAsyncComponent, computed, useI18n } from '#imports'
import type { RouteLocationRaw } from '#vue-router'

const YscIconsHeart = defineAsyncComponent(() => import('./YscIconsHeart.vue'))
const YscIconsHeartSolid = defineAsyncComponent(() => import('./YscIconsHeartSolid.vue'))
const YscIconsStarSolid = defineAsyncComponent(() => import('./YscIconsStarSolid.vue'))

export interface YscCamperCardV2Props {
  camperId: string | number
  title: string
  locationLabel: string
  passengersCount?: number
  bookingDailyPrice?: {
    value: number
    currency: string
  }
  bookingTotalPrice?: {
    value: number
    currency: string
  }
  bookingTotalPriceBeforeDiscount?: {
    value: number
    currency: string
  }
  startingPrice?: {
    value: number
    currency: string
  }
  camperUrl?: RouteLocationRaw
  rating?: number
  isBestOwner?: boolean
  isInWishlist?: boolean
  isNew?: boolean
  showWishlistButton?: boolean
  hasDiscount?: boolean
  hasInstantBooking?: boolean
  titleTag?: 'h2' | 'h3'
  showLink?: boolean
}

const props = withDefaults(defineProps<YscCamperCardV2Props>(), {
  passengersCount: undefined,
  bookingDailyPrice: undefined,
  bookingTotalPrice: undefined,
  bookingTotalPriceBeforeDiscount: undefined,
  startingPrice: undefined,
  rating: undefined,
  camperUrl: undefined,
  isBestOwner: false,
  isInWishlist: false,
  isNew: false,
  showWishlistButton: false,
  hasDiscount: false,
  hasInstantBooking: false,
  titleTag: 'h2',
  showLink: true,
})

defineEmits<{
  'wishlist-click': [camperId: string | number]
}>()

const { n, t } = useI18n()

const formattedRating = computed(() => {
  if (!props.rating) return undefined

  return n(props.rating, { maximumFractionDigits: 1, minimumFractionDigits: 1 })
})
</script>

<template>
  <article class="relative flex w-full flex-col overflow-hidden rounded">
    <div class="relative overflow-hidden rounded-md">
      <div
        v-if="hasDiscount"
        class="absolute left-2 top-2 z-10 flex flex-col items-baseline space-y-1"
      >
        <span class="rounded-xs bg-white px-1.5 py-1 text-xs font-semibold text-pink-500">
          {{ $t('camper_card.discount_special_offer') }}
        </span>
      </div>
      <button
        v-if="showWishlistButton"
        :aria-label="$t('commons.add-to-wishlist')"
        class="focus:ring-peacock-300 absolute right-1.5 top-1.5 z-30 p-1.5 focus:rounded-sm focus:outline-none focus:ring-2"
        name="wishlist"
        @click.prevent="$emit('wishlist-click', camperId)"
      >
        <YscIconsHeartSolid
          v-if="isInWishlist"
          class="relative top-px h-6 w-6 text-pink-500"
        />
        <YscIconsHeart
          v-else
          class="relative top-px h-6 w-6 fill-black/40 text-white"
        />
      </button>

      <NuxtLink
        :to="camperUrl"
        target="_blank"
        class="focus-visible:ring-peacock-300 block border-0 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        <span
          v-if="showLink"
          class="absolute inset-0 z-[-1]"
          aria-hidden="true"
        />
        <slot name="slideshow" />
      </NuxtLink>
    </div>
    <div class="relative px-1.5 py-3">
      <NuxtLink
        :to="props.camperUrl"
        target="_blank"
        class="focus-visible:ring-peacock-300 block border-0 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-4"
      >
        <span
          class="absolute inset-0 z-10"
          aria-hidden="true"
        />
        <component
          :is="props.titleTag"
          class="truncate text-base font-semibold"
        >
          {{ title }}
        </component>
      </NuxtLink>

      <div class="flex justify-between">
        <div class="text-left">
          <p class="text-sm">
            {{ locationLabel }}
          </p>
          <p class="text-sm">
            {{ passengersCount ? $t('commons.travelers_plural', { count_number: passengersCount }, passengersCount) : '' }}
          </p>
          <p class="text-sm flex gap-[6px] items-center">
            <span class="flex items-center gap-1 text-sm">
              <template v-if="formattedRating">
                <YscIconsStarSolid class="w-[0.875rem] h-[0.875rem] text-pink-500" /> <span class="font-semibold">{{ formattedRating }}</span>
              </template>
              <template v-else>
                <YscIconsStarSolid class="w-[0.875rem] h-[0.875rem] text-gray-400" /> <span class="text-gray-400">{{ isNew ? t('commons.new') : t('commons.no_reviews') }}</span>
              </template>
            </span>
            <span
              v-if="isBestOwner"
              class="text-yellow-700 text-xs font-semibold"
            >Best Owner</span>
          </p>
        </div>

        <div class="whitespace-nowrap flex flex-col justify-end items-end gap-[2px] mt-[-3px]">
          <YscIconsFlashSolid
            v-if="hasInstantBooking"
            class="h-5 w-5 text-amber-400"
          />
          <YscCamperCardPriceDisplay
            :booking-daily-price="bookingDailyPrice"
            :booking-total-price="bookingTotalPrice"
            :starting-price="startingPrice"
            :booking-total-price-before-discount="bookingTotalPriceBeforeDiscount"
          />
        </div>
      </div>
    </div>
  </article>
</template>
